import { useMemo, useRef, useState } from "react";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";
import { Glyph } from "@apf/design-system/glyphs";
import {
  businessModulePath,
  domainWorkblockPath,
  jobCatalogue,
  jobsForRole,
  roleDefinitions,
  rolePath,
  workMomentsForRole,
  workMomentPath
} from "../catalogue";
import { useCopy, useFramework } from "../framework";
import { CatalogueFacet, type CatalogueFacetLabels } from "../components/catalogue";
import { AbsenceBlock, EmptyState, PageHeader, PlaceLine, StatusMark } from "../components/ui";
import { PlaceTrail } from "../components/PlaceTrail";

function normalise(value: string): string {
  return value.normalize("NFKC").trim().toLocaleLowerCase();
}

function setOrDelete(params: URLSearchParams, key: string, value: string): URLSearchParams {
  const next = new URLSearchParams(params);
  if (value) next.set(key, value);
  else next.delete(key);
  return next;
}

type CopyResolver = (key: string, fallback?: string) => string;

function formatCopy(template: string, values: Readonly<Record<string, string | number>>): string {
  return Object.entries(values).reduce(
    (message, [name, value]) => message.replaceAll(`{${name}}`, String(value)),
    template
  );
}

function countLabel(count: number, singular: string, plural: string): string {
  return formatCopy(count === 1 ? singular : plural, { count });
}

function domainCount(copy: CopyResolver, count: number): string {
  return countLabel(
    count,
    copy("catalogue.count.domain-one", "{count} domain"),
    copy("catalogue.count.domain-many", "{count} domains")
  );
}

function jobCount(copy: CopyResolver, count: number): string {
  return countLabel(
    count,
    copy("catalogue.count.job-one", "{count} job"),
    copy("catalogue.count.job-many", "{count} jobs")
  );
}

function roleCount(copy: CopyResolver, count: number): string {
  return countLabel(
    count,
    copy("catalogue.count.role-one", "{count} role"),
    copy("catalogue.count.role-many", "{count} roles")
  );
}

function situationCount(copy: CopyResolver, count: number): string {
  return countLabel(
    count,
    copy("catalogue.count.situation-one", "{count} situation"),
    copy("catalogue.count.situation-many", "{count} situations")
  );
}

function metadataPair(copy: CopyResolver, first: string, second: string): string {
  return formatCopy(copy("catalogue.metadata-pair", "{first} · {second}"), { first, second });
}

function frameworkVersion(copy: CopyResolver, version: string): string {
  return formatCopy(copy("catalogue.framework-version", "Framework {version}"), { version });
}

export interface RolesCatalogueReturnState {
  catalogue: "roles";
  returnTo: string;
}

function rolesCatalogueReturnState(value: unknown): RolesCatalogueReturnState | undefined {
  if (!value || typeof value !== "object") return undefined;
  const candidate = value as Partial<RolesCatalogueReturnState>;
  if (candidate.catalogue !== "roles" || typeof candidate.returnTo !== "string") return undefined;
  if (!candidate.returnTo.startsWith("/reference/roles") || candidate.returnTo.startsWith("//")) return undefined;
  try {
    const url = new URL(candidate.returnTo, "https://groundworks.local");
    return url.origin === "https://groundworks.local" && url.pathname === "/reference/roles"
      ? { catalogue: "roles", returnTo: `${url.pathname}${url.search}${url.hash}` }
      : undefined;
  } catch {
    return undefined;
  }
}

export function RolesReferencePage() {
  const { snapshot } = useFramework();
  const copy = useCopy();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const domainId = searchParams.get("domain") ?? "";
  const searchRef = useRef<HTMLInputElement>(null);
  const clearFilters = () => {
    setSearchParams({});
    searchRef.current?.focus();
  };
  const roles = useMemo(() => roleDefinitions(snapshot).filter(({ definition, placements }) => {
    if (domainId && !placements.some(({ domainWorkblock }) => domainWorkblock.id === domainId)) return false;
    const text = [
      copy(definition.labelKey),
      copy(definition.descriptionKey),
      ...(definition.searchTermKeys ?? []).map((key) => copy(key)),
      ...placements.flatMap(({ domainWorkblock, role }) => [copy(domainWorkblock.nameKey), copy(role.roleKey), copy(role.mandateKey)])
    ].join(" ");
    return !query || normalise(text).includes(normalise(query));
  }).sort((left, right) => copy(left.definition.labelKey).localeCompare(copy(right.definition.labelKey))), [copy, domainId, query, snapshot]);
  const initials = [...new Set(roles.map(({ definition }) => copy(definition.labelKey).charAt(0).toLocaleUpperCase()))];
  const fromRolesCatalogue = `/reference/roles${location.search}`;

  return (
    <div className="apf-page apf-register-reference apf-catalogue-page">
      <PageHeader
        eyebrow={copy("catalogue.roles.eyebrow", "Reference")}
        title={copy("catalogue.roles.title", "Roles across source-to-pay")}
        description={copy("catalogue.roles.description", "Browse each human role once, then open its domain mandates, related jobs and Work moments.")}
        meta={<span>{formatCopy(copy("catalogue.roles.result-count", "{count} of {total} roles"), { count: roles.length, total: roleDefinitions(snapshot).length })}</span>}
      />
      <PlaceLine
        section={copy("catalogue.roles.section", "Roles")}
        segments={[{ text: formatCopy(copy("catalogue.visible-count", "{count} visible"), { count: roles.length }) }]}
        cut={frameworkVersion(copy, snapshot.frameworkVersion)}
      />
      <div className="apf-catalogue-controls" role="search" aria-label={copy("catalogue.roles.filter-label", "Filter roles")}>
        <label className="apf-catalogue-control"><span>{copy("catalogue.roles.search-label", "Search roles")}</span><input ref={searchRef} type="search" value={query} onChange={(event) => setSearchParams(setOrDelete(searchParams, "q", event.target.value), { replace: true })} /></label>
        <label className="apf-catalogue-control"><span>{copy("catalogue.filter.domain", "Domain")}</span><select value={domainId} onChange={(event) => setSearchParams(setOrDelete(searchParams, "domain", event.target.value))}><option value="">{copy("catalogue.filter.all-domains", "All domains")}</option>{snapshot.content.domainWorkblocks.map((domain) => <option key={domain.id} value={domain.id}>{copy(domain.nameKey)}</option>)}</select></label>
        {query || domainId ? <button className="apf-button apf-button--quiet" type="button" onClick={clearFilters}>{copy("catalogue.clear-filters", "Clear filters")}</button> : null}
      </div>
      {initials.length ? <nav className="apf-a-z" aria-label={copy("catalogue.roles.initials-label", "Role initials")}>{initials.map((initial) => <a href={`#role-initial-${initial}`} key={initial}>{initial}</a>)}</nav> : null}
      {roles.length ? (
        <ol className="apf-catalogue-index">
          {roles.map(({ definition, placements, relatedBusinessModuleIds }, index) => {
            const label = copy(definition.labelKey);
            const previousInitial = index ? copy(roles[index - 1]!.definition.labelKey).charAt(0).toLocaleUpperCase() : "";
            const initial = label.charAt(0).toLocaleUpperCase();
            return (
              <li className="apf-catalogue-index__row" id={initial !== previousInitial ? `role-initial-${initial}` : undefined} key={definition.id}>
                <Link className="apf-catalogue-index__link" to={rolePath(definition.id)} state={{ catalogue: "roles", returnTo: fromRolesCatalogue }}>
                  <span className="apf-catalogue-index__heading"><Glyph meaning="class.role" size={16} /><strong>{label}</strong></span>
                  <span>{copy(definition.descriptionKey)}</span>
                  <span className="apf-catalogue-index__meta">{metadataPair(copy, domainCount(copy, placements.length), jobCount(copy, relatedBusinessModuleIds.length))}</span>
                </Link>
              </li>
            );
          })}
        </ol>
      ) : <EmptyState title={copy("catalogue.roles.empty-title", "No roles match")} description={copy("catalogue.roles.empty-description", "Clear a filter or search for another role or domain.")} action={<button className="apf-button" type="button" onClick={clearFilters}>{copy("catalogue.clear-filters", "Clear filters")}</button>} />}
    </div>
  );
}

export function RoleDetailPage() {
  const { roleId = "" } = useParams();
  const { snapshot } = useFramework();
  const copy = useCopy();
  const location = useLocation();
  const returnState = rolesCatalogueReturnState(location.state);
  const allRoles = roleDefinitions(snapshot);
  const entry = allRoles.find(({ definition }) => definition.id === roleId);
  const [openPlacementByRole, setOpenPlacementByRole] = useState<Record<string, string | null>>({});
  if (!entry) return <div className="apf-page apf-register-reference"><AbsenceBlock eyebrow={copy("absence.role.eyebrow", "Role reference")} title={copy("catalogue.role-detail.not-found-title", "Role not found")} support={copy("catalogue.role-detail.not-found-description", "This role is not part of the current reference.")} headingLevel={1} holds={[{ label: copy("absence.holding.roles", "Roles"), count: allRoles.length }, { label: copy("absence.holding.workblocks", "Domain Workblocks"), count: snapshot.content.domainWorkblocks.length }, { label: copy("absence.holding.business-modules", "Business modules"), count: snapshot.businessModuleRegistry.businessModules.length }]} routes={<Link to="/reference/roles">{copy("catalogue.role-detail.return-to-roles", "Return to roles")}</Link>} /></div>;
  const jobs = jobsForRole(snapshot, roleId);
  const performedJobIds = new Set(entry.placements.flatMap(({ role }) => role.performsBusinessModuleIds));
  const consultedJobIds = new Set(entry.placements.flatMap(({ role }) => role.consultedBusinessModuleIds));
  const performedJobs = jobs.filter(({ id }) => performedJobIds.has(id));
  const consultedJobs = jobs.filter(({ id }) => consultedJobIds.has(id));
  const domainsById = new Map(snapshot.content.domainWorkblocks.map((domain) => [domain.id, domain]));
  const moments = workMomentsForRole(snapshot, roleId).slice(0, 3);
  const openPlacementId = Object.prototype.hasOwnProperty.call(openPlacementByRole, roleId)
    ? openPlacementByRole[roleId]
    : entry.placements[0]?.domainWorkblock.id ?? null;
  return (
    <div className="apf-page apf-register-reference apf-role-detail">
      <Link className="apf-button apf-button--quiet apf-back-link" to={returnState?.returnTo ?? "/reference/roles"}>{copy("catalogue.role-detail.back-to-roles", "Back to roles")}</Link>
      <PlaceTrail stops={[
        { label: copy("component.place-trail.framework", "Framework"), to: "/" },
        { label: copy("nav.roles", "Roles"), to: "/reference/roles" },
        { label: copy(entry.definition.labelKey) }
      ]} />
      <PageHeader eyebrow={copy("catalogue.role-detail.eyebrow", "Role")} title={copy(entry.definition.labelKey)} description={copy(entry.definition.descriptionKey)} meta={<span>{metadataPair(copy, domainCount(copy, entry.placements.length), `${formatCopy(copy("catalogue.role-detail.performs-count", "performs {count}"), { count: String(performedJobs.length) })} · ${formatCopy(copy("catalogue.role-detail.consulted-count", "consulted on {count}"), { count: String(consultedJobs.length) })}`)}</span>} />
      <section className="apf-page-section" aria-labelledby="role-placements-title"><h2 id="role-placements-title">{copy("catalogue.role-detail.placements-title", "Mandate and authority by domain")}</h2><div className="apf-disclosure-list">{entry.placements.map(({ domainWorkblock, role }) => <details
        key={domainWorkblock.id}
        id={`placement-${domainWorkblock.id}`}
        open={openPlacementId === domainWorkblock.id || location.hash === `#placement-${domainWorkblock.id}`}
        onToggle={(event) => {
          const placementId = domainWorkblock.id;
          setOpenPlacementByRole((current) => {
            if (event.currentTarget.open) return { ...current, [roleId]: placementId };
            const currentPlacementId = Object.prototype.hasOwnProperty.call(current, roleId)
              ? current[roleId]
              : entry.placements[0]?.domainWorkblock.id ?? null;
            return currentPlacementId === placementId ? { ...current, [roleId]: null } : current;
          });
        }}
      ><summary>{copy(domainWorkblock.nameKey)}</summary><div className="apf-role-placement"><p className="apf-role-placement__mandate">{copy(role.mandateKey)}</p><p className="apf-role-placement__question">{copy(role.questionKey)}</p><dl className="apf-definition-list"><div><dt>{copy("catalogue.role-detail.measured-on", "Measured on")}</dt><dd>{copy(role.measuredKey)}</dd></div><div><dt>{copy("catalogue.role-detail.may-propose", "May propose")}</dt><dd>{copy(role.proposeKey)}</dd></div><div><dt>{copy("catalogue.role-detail.may-commit", "May commit")}</dt><dd>{copy(role.commitKey)}</dd></div><div><dt>{copy("catalogue.role-detail.never-alone", "Never alone")}</dt><dd>{copy(role.aloneKey)}</dd></div></dl><p><Link to={domainWorkblockPath(domainWorkblock.id)}>{formatCopy(copy("catalogue.role-detail.open-domain", "Open {name}"), { name: copy(domainWorkblock.nameKey) })}</Link></p></div></details>)}</div></section>
      <section className="apf-page-section" aria-labelledby="role-performed-jobs-title">
        <h2 id="role-performed-jobs-title">{copy("catalogue.role-detail.performed-jobs-title", "Jobs this role performs")}</h2>
        {performedJobs.length ? <ul className="apf-catalogue-index">{performedJobs.map((job) => {
          const domainWorkblock = domainsById.get(job.domainWorkblockId);
          return <li className="apf-catalogue-index__row" key={job.id}><Link className="apf-catalogue-index__link" to={businessModulePath(job.domainWorkblockId, job.id)}><strong>{copy(job.labelKey)}</strong><span>{copy(job.jobStatementKey)}</span>{domainWorkblock ? <span className="apf-catalogue-index__meta">{copy(domainWorkblock.nameKey)}</span> : null}</Link></li>;
        })}</ul> : <p>{copy("catalogue.role-detail.performed-jobs-empty", "No performed jobs are connected to this role.")}</p>}
      </section>
      <section className="apf-page-section" aria-labelledby="role-consulted-jobs-title">
        <h2 id="role-consulted-jobs-title">{copy("catalogue.role-detail.consulted-jobs-title", "Jobs where this role is consulted")}</h2>
        {consultedJobs.length ? <ul className="apf-catalogue-index">{consultedJobs.map((job) => {
          const domainWorkblock = domainsById.get(job.domainWorkblockId);
          return <li className="apf-catalogue-index__row" key={job.id}><Link className="apf-catalogue-index__link" to={businessModulePath(job.domainWorkblockId, job.id)}><strong>{copy(job.labelKey)}</strong><span>{copy(job.jobStatementKey)}</span>{domainWorkblock ? <span className="apf-catalogue-index__meta">{copy(domainWorkblock.nameKey)}</span> : null}</Link></li>;
        })}</ul> : <p>{copy("catalogue.role-detail.consulted-jobs-empty", "No consulted jobs are connected to this role.")}</p>}
      </section>
      <p><Link to={`/reference/jobs?role=${encodeURIComponent(roleId)}`}>{copy("catalogue.role-detail.browse-jobs", "Browse this role's jobs in the catalogue")}</Link></p>
      {moments.length ? <section className="apf-page-section" aria-labelledby="role-moments-title"><h2 id="role-moments-title">{copy("catalogue.role-detail.work-moments-title", "See this role at work")}</h2><ul className="apf-catalogue-index">{moments.map((moment) => <li className="apf-catalogue-index__row" key={moment.id}><Link className="apf-catalogue-index__link" to={workMomentPath(moment.id, roleId)}><strong>{copy(moment.titleKey)}</strong><span>{copy(moment.summaryKey)}</span></Link></li>)}</ul><p><Link to={`/reference/work-moments?role=${encodeURIComponent(roleId)}`}>{copy("catalogue.role-detail.browse-work-moments", "Browse all work moments for this role")}</Link></p></section> : null}
      <p className="apf-detail-actions"><Link className="apf-button" to={`/workblocks?role=${encodeURIComponent(roleId)}`}>{copy("catalogue.role-detail.find-workblocks", "Find Workblocks for this role")}</Link></p>
    </div>
  );
}

export function JobsReferencePage() {
  const { snapshot } = useFramework();
  const copy = useCopy();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const domainId = searchParams.get("domain") ?? "";
  const roleId = searchParams.get("role") ?? "";
  const [roleFacetOpen, setRoleFacetOpen] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const catalogue = useMemo(() => jobCatalogue(snapshot), [snapshot]);
  const candidateJobs = useMemo(() => catalogue.filter(({ businessModule, domainWorkblock }) => {
    if (domainId && domainWorkblock.id !== domainId) return false;
    const text = [copy(businessModule.labelKey), copy(businessModule.jobStatementKey), copy(domainWorkblock.nameKey), ...businessModule.jobStories.flatMap((story) => [copy(story.storyKey), copy(story.completionKey)])].join(" ");
    return !query || normalise(text).includes(normalise(query));
  }), [catalogue, copy, domainId, query]);
  const jobs = useMemo(() => candidateJobs.filter(({ roles: connectedRoles }) =>
    !roleId || connectedRoles.some(({ definition }) => definition.id === roleId)
  ), [candidateJobs, roleId]);
  const grouped = snapshot.content.domainWorkblocks.map((domain) => ({ domain, jobs: jobs.filter(({ domainWorkblock }) => domainWorkblock.id === domain.id) })).filter(({ jobs }) => jobs.length);
  const roles = useMemo(() => roleDefinitions(snapshot), [snapshot]);
  const roleOptions = useMemo(() => roles.map(({ definition, placements }) => ({
    id: definition.id,
    label: copy(definition.labelKey),
    context: domainCount(copy, placements.length),
    count: candidateJobs.filter(({ roles: connectedRoles }) =>
      connectedRoles.some(({ definition: connectedDefinition }) => connectedDefinition.id === definition.id)
    ).length
  })).sort((left, right) => left.label.localeCompare(right.label)), [candidateJobs, copy, roles]);
  const projectedRoleIds = new Set(roleOptions.map(({ id }) => id));
  const selectedRoleIds = roleId ? [roleId] : [];
  const unavailableRoleCount = roleId && !projectedRoleIds.has(roleId) ? 1 : 0;
  const facetLabels: CatalogueFacetLabels = {
    search: (label) => formatCopy(copy("component.facet.search", "Search {label}"), { label }),
    clear: (label) => formatCopy(copy("component.facet.clear", "Clear {label}"), { label }),
    clearAll: copy("component.facet.clear-all", "Clear all"),
    selectedCount: (count) => formatCopy(copy("component.facet.selected-count", "{count} selected"), { count }),
    moreCount: (count) => formatCopy(copy("component.facet.more-count", "{count} more"), { count }),
    noOptions: copy("component.facet.no-options", "No matching options."),
    tryAgain: copy("component.facet.try-again", "Try again."),
    resultCount: (count) => formatCopy(copy("component.facet.result-count", "{count} options match."), { count }),
    optionCount: (count) => formatCopy(copy("component.facet.option-count", "{count} matching records"), { count }),
    unavailableCount: (count) => formatCopy(copy("component.facet.unavailable-count", "{count} filters are unavailable in this view."), { count }),
    clearUnavailable: copy("component.facet.clear-unavailable", "Clear unavailable filters")
  };
  const fromJobsCatalogue = `/reference/jobs${location.search}`;
  const updateRole = (ids: readonly string[]) => {
    const nextRoleId = ids.at(-1) ?? "";
    setSearchParams(setOrDelete(searchParams, "role", nextRoleId), { preventScrollReset: true });
    if (!nextRoleId) searchRef.current?.focus();
  };
  const clearFilters = () => {
    setSearchParams({});
    searchRef.current?.focus();
  };
  return (
    <div className="apf-page apf-register-reference apf-catalogue-page">
      <PageHeader eyebrow={copy("catalogue.jobs.eyebrow", "Reference · Jobs to be done")} title={copy("catalogue.jobs.title", "The progress each business module owns")} description={copy("catalogue.jobs.description", "Browse one job per business module. Open the module for its situations, completion evidence, authority and execution flows.")} meta={<span>{formatCopy(copy("catalogue.jobs.result-count", "{count} of {total} jobs"), { count: jobs.length, total: jobCatalogue(snapshot).length })}</span>} />
      <PlaceLine section={copy("catalogue.jobs.section", "Jobs to be done")} segments={[{ text: formatCopy(copy("catalogue.visible-count", "{count} visible"), { count: jobs.length }) }]} cut={frameworkVersion(copy, snapshot.frameworkVersion)} />
      <div className="apf-catalogue-controls" role="search" aria-label={copy("catalogue.jobs.filter-label", "Filter jobs")}><label className="apf-catalogue-control"><span>{copy("catalogue.jobs.search-label", "Search jobs")}</span><input ref={searchRef} type="search" value={query} onChange={(event) => setSearchParams(setOrDelete(searchParams, "q", event.target.value), { replace: true })} /></label><label className="apf-catalogue-control"><span>{copy("catalogue.filter.domain", "Domain")}</span><select value={domainId} onChange={(event) => setSearchParams(setOrDelete(searchParams, "domain", event.target.value))}><option value="">{copy("catalogue.filter.all-domains", "All domains")}</option>{snapshot.content.domainWorkblocks.map((domain) => <option key={domain.id} value={domain.id}>{copy(domain.nameKey)}</option>)}</select></label>{query || domainId || roleId ? <button className="apf-button apf-button--quiet" type="button" onClick={clearFilters}>{copy("catalogue.clear-filters", "Clear filters")}</button> : null}</div>
      <div className="apf-catalogue-page__facets">
        <CatalogueFacet
          id="jobs-role"
          label={copy("catalogue.filter.role", "Role")}
          labels={facetLabels}
          options={roleOptions}
          selectedIds={selectedRoleIds}
          onSelectedIdsChange={updateRole}
          open={roleFacetOpen}
          onOpenChange={setRoleFacetOpen}
          unavailableSelectedCount={unavailableRoleCount}
          onClearUnavailable={() => updateRole([])}
          onClearAll={clearFilters}
        />
      </div>
      {grouped.length ? grouped.map(({ domain, jobs: domainJobs }) => <section className="apf-page-section apf-catalogue-group" key={domain.id}><h2>{copy(domain.nameKey)}</h2><ol className="apf-catalogue-index">{domainJobs.map(({ businessModule, roles: jobRoles }) => <li className="apf-catalogue-index__row" key={businessModule.id}><Link className="apf-catalogue-index__link" to={businessModulePath(domain.id, businessModule.id)} state={{ catalogue: "jobs", returnTo: fromJobsCatalogue }}><span className="apf-catalogue-index__heading"><Glyph meaning="class.job" size={16} /><strong>{copy(businessModule.labelKey)}</strong><StatusMark value={businessModule.implementationStatus} /></span><span>{copy(businessModule.jobStatementKey)}</span><span className="apf-catalogue-index__meta">{metadataPair(copy, roleCount(copy, jobRoles.length), situationCount(copy, businessModule.jobStories.length))}</span></Link></li>)}</ol></section>) : <EmptyState title={copy("catalogue.jobs.empty-title", "No jobs match")} description={copy("catalogue.jobs.empty-description", "Clear a filter or search for another job, role or domain.")} action={<button className="apf-button" type="button" onClick={clearFilters}>{copy("catalogue.clear-filters", "Clear filters")}</button>} />}
    </div>
  );
}
